import { TILE, type Tuning } from '../config/tuning'
import type { ArmamentId, EnemyType, Entity, PickupType } from '../ecs/entity'
import { makeEntity } from '../ecs/entity'
import type { InputSource } from '../input/types'
import { emptyIntent } from '../input/types'

export const tileCenter = (t: number): number => t * TILE + TILE / 2

export function makePlayer(tuning: Tuning, source: InputSource, tx: number, ty: number): Entity {
  const e = makeEntity('player', tileCenter(tx), tileCenter(ty), tuning.player.radius, 'player')
  e.faction = 'participant'
  e.hp = tuning.player.maxHp
  e.maxHp = tuning.player.maxHp
  e.invuln = tuning.player.invulnAfterSpawn
  e.player = {
    intent: emptyIntent(),
    source,
    magic: tuning.player.startMagic,
    keys: 0,
    fireT: 0,
    contactT: 0,
    starvingEventT: 0,
    altT: 0
  }
  return e
}

export function makeEnemy(tuning: Tuning, type: EnemyType, x: number, y: number, parentGen?: number, band = 0): Entity {
  const cfg = tuning.enemies[type]
  const e = makeEntity('enemy', x, y, cfg.radius, `enemy_${type}`)
  e.faction = 'network'
  e.etype = type
  e.spawnT = 0.35
  e.ai = { state: 'seek', t: 0, tx: x, ty: y, cooldown: 0, data: {} }
  e.contactT = 0
  if (type !== 'auditor') {
    const mortal = tuning.enemies[type]
    e.hp = mortal.hp
    e.maxHp = mortal.hp
    // deeper bands move faster — speed only, hp/damage stay flat (the auditor has its own ramp)
    e.speedMul = 1 + band * tuning.enemies.speedBandScale
  }
  if (type === 'static' || type === 'auditor') e.wallClipped = false
  if (parentGen !== undefined) e.parentGen = parentGen
  return e
}

export function makeGenerator(
  tuning: Tuning,
  type: Exclude<EnemyType, 'auditor'>,
  tx: number,
  ty: number,
  band: number,
  overrides?: { budget?: number; interval?: number }
): Entity {
  const g = tuning.generators
  const hp = Math.round(g.baseHp * (1 + band * g.hpBandScale))
  const interval = overrides?.interval ?? Math.max(1.1, g.spawnInterval * (1 - band * g.spawnIntervalBandScale))
  const e = makeEntity('generator', tileCenter(tx), tileCenter(ty), 14, `gen_${type}`)
  e.faction = 'network'
  e.hp = hp
  e.maxHp = hp
  e.gen = {
    genType: type,
    interval,
    t: interval * 0.5,
    budget: overrides?.budget ?? Infinity,
    aliveCap: g.aliveCap,
    boost: 1
  }
  return e
}

export function makePickup(type: PickupType, tx: number, ty: number, armament?: ArmamentId): Entity {
  const e = makeEntity('pickup', tileCenter(tx), tileCenter(ty), 9, `pickup_${type}`)
  e.pickup = { ptype: type, value: 1, ...(armament ? { armament } : {}) }
  return e
}

export function makeProjectile(
  tuning: Tuning,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  speed: number,
  damage: number,
  faction: 'participant' | 'network'
): Entity {
  const e = makeEntity('projectile', x, y, faction === 'participant' ? 4 : 5, faction === 'participant' ? 'proj_player' : 'proj_enemy')
  e.vx = dirX * speed
  e.vy = dirY * speed
  e.facing = Math.atan2(dirY, dirX)
  e.proj = { damage, faction }
  e.lifetime = tuning.projectiles.lifetime
  e.wallClipped = true
  return e
}

export function makeCorpse(of: Exclude<EnemyType, 'auditor'>, x: number, y: number): Entity {
  const e = makeEntity('corpse', x, y, 8, `corpse_${of}`)
  e.corpseOf = of
  e.lifetime = 10
  return e
}
